// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Enable debug logs only in development
  const DEBUG = process.env.NODE_ENV === 'development' && false; // Set to true to enable debug logs

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    if (DEBUG) console.log('Checking auth status...');
    try {
      // Check for user info from different storage keys
      const isLoggedIn = localStorage.getItem('isLoggedIn');
      const userInfo = localStorage.getItem('userInfo');
      const winnestUser = localStorage.getItem('winnest_user');

      if (DEBUG) console.log('Storage check:', { isLoggedIn, userInfo: !!userInfo, winnestUser: !!winnestUser });

      if (isLoggedIn === 'true' && userInfo) {
        // If logged in via AdminLogin component
        const userData = JSON.parse(userInfo);
        if (DEBUG) console.log('Found userInfo in localStorage:', userData);
        setUser({
          ...userData,
          role: 'admin' // Assuming admin login always gives admin role
        });
      } else if (winnestUser) {
        // If logged in via previous method
        const userData = JSON.parse(winnestUser);
        if (DEBUG) console.log('Found winnestUser in localStorage:', userData);
        setUser(userData);
      } else {
        if (DEBUG) console.log('No authentication data found');
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
      // Clear corrupted data
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('winnest_user');
    } finally {
      setLoading(false);
      if (DEBUG) console.log('Auth check completed');
    }
  };

  const login = (userData, rememberMe = false) => {
    const DEBUG = process.env.NODE_ENV === 'development' && false;
    if (DEBUG) console.log('Login called with:', userData, rememberMe);
    const user = {
      id: userData.id || 1,
      email: userData.email || userData.username,
      name: userData.name || userData.username,
      role: userData.role || 'admin',
      username: userData.username
    };

    if (DEBUG) console.log('Setting user:', user);
    setUser(user);

    if (rememberMe) {
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userInfo', JSON.stringify(user));
      if (DEBUG) console.log('Saved to localStorage with remember me');
    }
    
    // Also set winnest_user for compatibility
    localStorage.setItem('winnest_user', JSON.stringify(user));
    if (DEBUG) console.log('Saved winnest_user to localStorage');
    
    return { success: true };
  };

  const logout = () => {
    const DEBUG = process.env.NODE_ENV === 'development' && false;
    if (DEBUG) console.log('Logout called');
    setUser(null);
    // Clear all auth-related localStorage items
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('winnest_user');
    localStorage.removeItem('username');
    localStorage.removeItem('authToken'); // Clear JWT token
    if (DEBUG) console.log('Cleared all auth data from localStorage');
  };

  const isAdmin = () => {
    const DEBUG = process.env.NODE_ENV === 'development' && false;
    const result = user?.role === 'admin';
    if (DEBUG) console.log('isAdmin check:', result, 'user role:', user?.role);
    return result;
  };

  const isAuthenticated = () => {
    const DEBUG = process.env.NODE_ENV === 'development' && false;
    const result = !!user;
    if (DEBUG) console.log('isAuthenticated check:', result, 'user exists:', !!user);
    return result;
  };

  // Function to update auth status (useful for login success)
  const updateAuthStatus = (userData, rememberMe = false) => {
    const DEBUG = process.env.NODE_ENV === 'development' && false;
    if (DEBUG) console.log('updateAuthStatus called');
    return login(userData, rememberMe);
  };

  const value = {
    user,
    login,
    logout,
    isAdmin,
    isAuthenticated,
    loading,
    updateAuthStatus,
    checkAuthStatus
  };

  if (DEBUG) console.log('AuthProvider rendering with user:', user, 'loading:', loading);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};